/**
 * ============================================================================
 * OBJECTS STORE - DELETE OPERATIONS
 * ============================================================================
 * 
 * Handles deleting objects via the backend API.
 * 
 * Features:
 * - Automatic deprecation of related translation strings
 * - Graceful handling of string deprecation failures
 * 
 * @module objects/api/delete
 */

import { apiService } from "@/lib/api-functions/api-service";
import { objectsStore } from "../store";
import type { ObjectItem, ToastFunction } from "../types";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Collect all string IDs from an object's translatable fields.
 * Used to mark related strings as deprecated when deleting an object.
 */
function collectStringIds(object: ObjectItem): string[] {
  const stringIds: string[] = [];
  
  object.data.forEach(section => {
    section.fields.forEach(field => {
      // Handle single translatable fields (legacy format)
      if (field.string_id && field.translation) {
        stringIds.push(field.string_id);
      }
      
      // Handle multiple translatable fields (TranslatableValue[] format)
      if (field.translation && field.multiple && Array.isArray(field.value)) {
        field.value.forEach((item: any) => {
          if (item && typeof item === 'object' && 'string_id' in item && item.string_id) {
            stringIds.push(item.string_id);
          }
        });
      }
    });
  });
  
  return stringIds;
}

// ============================================================================
// DELETE OPERATIONS
// ============================================================================

/**
 * Delete an object via API and sync the store.
 * 
 * Process:
 * 1. Collect all related string IDs
 * 2. Delete the object via API
 * 3. Mark related strings as deprecated
 * 4. Remove from local store
 * 
 * @param id - The object ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for notifications
 * 
 * @example
 * ```ts
 * await deleteObject('object-123', 'game-456', toast);
 * ```
 */
export async function deleteObject(
  id: string,
  gameId: string,
  toast?: ToastFunction
): Promise<void> {
  try {
    // Get the object data before deletion to find related strings
    const currentData = objectsStore.getData();
    const objectToDelete = currentData.find(item => item.id === id);
    
    if (!objectToDelete) {
      throw new Error('Object not found in store');
    }

    // Collect all string IDs that need to be marked as deprecated
    const stringIdsToDeprecate = collectStringIds(objectToDelete);

    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Delete via API
    const result = await apiService.deleteObject(id, gameId);

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete object');
    }

    // ======================================================================
    // DEPRECATE RELATED STRINGS
    // ======================================================================
    if (stringIdsToDeprecate.length > 0) {
      try {
        const { markStringsAsDeprecated } = await import('../../stringsStore');
        await markStringsAsDeprecated(stringIdsToDeprecate, gameId, toast);
        
        console.log(`[deleteObject] Deprecated ${stringIdsToDeprecate.length} related strings`);
        
        if (toast) {
          toast({
            title: "Success",
            description: `Object deleted and ${stringIdsToDeprecate.length} related strings marked as deprecated`,
          });
        }
      } catch (deprecationError) {
        console.error('[deleteObject] Failed to mark strings as deprecated:', deprecationError);
        // Don't fail the entire deletion if deprecation fails
        if (toast) {
          toast({
            title: "Warning",
            description: "Object deleted but failed to mark related strings as deprecated",
            variant: "destructive",
          });
        }
      }
    } else {
      console.log('[deleteObject] No translatable strings found to deprecate');
      if (toast) {
        toast({
          title: "Success",
          description: result.data?.message || "Object deleted successfully",
        });
      }
    }

    // ======================================================================
    // UPDATE LOCAL STORE
    // ======================================================================
    const updatedData = currentData.filter(item => item.id !== id);
    objectsStore.setData(updatedData);

    console.log(`[deleteObject] Deleted object: ${id}`);
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete object';
    console.error('[deleteObject] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
